import { getSettingDataV1, getClinicRoles } from "@/api/myPractice"

// Permissions come from two sources that arrive independently:
//   getUserInfoV2      -> user_rolesv2: which roles the user holds in this clinic
//   /role/clinic-roles -> the catalog of every role and the resources it grants
// Either can land first, so both are kept and `permissions` is recomputed
// whenever one of them changes.

// RBAC names are the RBAC system's own uppercase names (PROVIDER / STAFF /
// DELEGATE / CLINIC_ADMIN_ADDON) - not interchangeable with the lowercase role
// names the invite/edit payloads use.
const rbacName = (value) => value?.toString().toUpperCase()

// The RBAC permission API (/role/clinic-roles + user_rolesv2) is only rolled out
// to clinics on this beta program. Everyone else keeps the pre-RBAC behaviour:
// an admin edits everything, anyone else gets the same read-only view as before.
const RBAC_BETA_PROGRAM = 'general_beta1'

// The user_rolesv2 entry for the clinic the user info was issued for.
const extractClinicEntry = (data) => {
    const entries = Array.isArray(data?.user_rolesv2) ? data.user_rolesv2 : []
    const clinicId = data?.clinic_id?.toString()
    // A lone entry belongs to the signed-in clinic even if its clinic_id doesn't match.
    return entries.find(e => e?.clinic_id?.toString() === clinicId)
        || (entries.length === 1 ? entries[0] : null)
}

// The RBAC roles the user holds in that clinic. `roles` already carries the
// add-ons (CLINIC_ADMIN_ADDON / DELEGATE), so the is_admin / is_delegate flags
// alongside it are not consulted here - only the legacy gate reads is_admin.
const extractUserRoles = (entry) => (Array.isArray(entry?.roles) ? entry.roles : []).filter(Boolean).map(rbacName)

// The only actions the front end acts on. Anything else the API returns (the
// retired VIEW, for one) is dropped at load so nothing can start reading it.
const RBAC_ACTIONS = ['ACCESS', 'EDIT']

// The clinic-roles catalog as { PROVIDER: { ACCESS: [resource, ...], EDIT: [...] }, ... }
const extractRoleCatalog = (response) => {
    const roles = Array.isArray(response?.data?.roles) ? response.data.roles : []
    return roles.reduce((catalog, role) => {
        const name = rbacName(role?.internal_name || role?.name)
        if (!name) return catalog
        const actions = {}
        const permissions = Array.isArray(role?.permissions) ? role.permissions : []
        permissions.forEach(permission => {
            const action = rbacName(permission?.action)
            if (!RBAC_ACTIONS.includes(action)) return
            const resources = (Array.isArray(permission?.resources) ? permission.resources : [])
                .filter(Boolean).map(rbacName)
            actions[action] = [...new Set([...(actions[action] || []), ...resources])]
        })
        catalog[name] = actions
        return catalog
    }, {})
}

// The union of what the user's roles grant, as { RESOURCE: [action, ...] }. The
// same resource can be granted at different levels by different roles (PROVIDER
// only ACCESSes MYPRACTICE-PRACTICE-SETTING, CLINIC_ADMIN_ADDON also EDITs it), so
// every action is kept and the getters decide which one is enough.
const buildPermissions = (roles, catalog) => {
    const permissions = {}
    roles.forEach(role => {
        Object.entries(catalog[role] || {}).forEach(([action, resources]) => {
            resources.forEach(resource => {
                if (!permissions[resource]) permissions[resource] = []
                if (!permissions[resource].includes(action)) permissions[resource].push(action)
            })
        })
    })
    return permissions
}

// Navbar and the settings pages each dispatch getUserInfo on mount, so share one
// in-flight request instead of letting every caller fire its own.
let roleCatalogRequest = null

// Never rejects: permissions are supplementary, so a failure only logs and leaves
// rbacCatalogLoaded false (the next getRbacRoles dispatch retries) rather than
// putting the whole module into the error state.
const fetchRoleCatalog = (commit) => {
    if (!roleCatalogRequest) {
        roleCatalogRequest = getClinicRoles().finally(() => { roleCatalogRequest = null })
    }
    return roleCatalogRequest
        .then(response => { commit("SET_RBAC_ROLE_CATALOG", extractRoleCatalog(response)) })
        .catch(error => { console.log(error) })
}

const userInfo = {
    namespaced: true,
    state: {
        user_roles: [],
        // RBAC roles the user holds in the current clinic, read off getUserInfoV2.
        rbac_roles: [],
        // The same entry's is_admin flag - the whole permission model for
        // clinics that aren't on the RBAC beta (see legacyCanEdit).
        is_admin: false,
        // Role -> action -> resources, from /role/clinic-roles.
        rbac_role_catalog: {},
        rbacCatalogLoaded: false,
        // Resource -> actions, the two above mapped onto this user.
        permissions: {},
        internal_role: null,
        clinicId: "",
        oldClinicId: "",
        clientId: "",
        name: "",
        roleHasError: false,
        roleLoading: true,
        jwtToken: "",
        beta_program_enabled: false,
        beta_programs: [],
        customer_country: '',
    },
    getters: {
        // getUserInfoV2 states outright whether the user administers this clinic,
        // so nothing infers it from role names any more.
        isAdmin: state => state.is_admin,
        // Whether permissions come from RBAC at all. beta_programs is read off
        // getUserInfoV2, which lands in the login module first and is copied into
        // this module by SET_USERINFO - during bootstrap only the former is set.
        isRbacEnabled: (state, getters, rootState) => {
            const programs = state.beta_programs?.length
                ? state.beta_programs
                : rootState?.login?.userInfo?.beta_programs
            return Array.isArray(programs) && programs.includes(RBAC_BETA_PROGRAM)
        },
        // The fallback for clinics not on the RBAC beta: getUserInfoV2 says
        // outright whether the user administers this clinic, and an admin edits
        // every resource.
        legacyCanEdit: state => state.is_admin,
        rbacRoles: state => state.rbac_roles,
        hasRbacRole: state => role => state.rbac_roles.includes(rbacName(role)),
        permissions: state => state.permissions,
        // ACCESS lets the user see a resource (open its page, view its data).
        // Before RBAC nothing was hidden outright - non-admins saw every
        // practice tab, just not editable.
        canAccess: (state, getters) => resource => (
            getters.isRbacEnabled
                ? (state.permissions[rbacName(resource)] || []).includes('ACCESS')
                : true
        ),
        // EDIT lets the user change a resource. It is a separate grant from ACCESS,
        // so a role holding only ACCESS sees the page read-only.
        canEdit: (state, getters) => resource => (
            getters.isRbacEnabled
                ? (state.permissions[rbacName(resource)] || []).includes('EDIT')
                : getters.legacyCanEdit
        ),
        hasPermission: (state, getters) => (resource, action = 'ACCESS') => (
            rbacName(action) === 'EDIT' ? getters.canEdit(resource) : getters.canAccess(resource)
        ),
        isInternalUser: state => state.internal_role != null,
        isInternalAdmin: state => state.internal_role === 'admin',
        isCAM: state => state.internal_role === 'navigator',
        clinicId: state => state.clinicId,
        clientId: state => state.clientId,
        isBetaAllowed: (state, getters) => (program) => {
            return (state.beta_programs || []).includes(program)
                || String(getters.joinBetaProgram) === '1'
        },
        joinBetaProgram: (state, getters, rootState) => rootState?.myPractice?.generalPerferences?.old_practice?.joinBetaProgram,
    },
    mutations: {
        UPDATE_PRACTICE_NAME(state, practice_name) {
            state.name = practice_name
        },
        SET_DEFAULT(state) {
            state.user_roles = []
            state.rbac_roles = []
            state.is_admin = false
            state.rbac_role_catalog = {}
            state.rbacCatalogLoaded = false
            state.permissions = {}
            state.internal_role = null
            state.clinicId = ""
            state.oldClinicId = ""
            state.clientId = ""
            state.name = ""
            state.roleHasError = false
            state.roleLoading = true
            state.customerCountry = ""
        },
        SET_USERINFO(state, data) {
            // console.log(data)
            state.user_roles = data.user_roles;
            state.internal_role = data.internal_role;
            state.clinicId = data.clinic_id?.toString();
            state.oldClinicId = data.old_clinic_id?.toString();
            state.clientId = data.customer_id?.toString();
            state.original_customer_id = data.original_customer_id?.toString();
            state.name = data.clinic_name;
            state.beta_program_enabled = data.beta_program_enabled;
            state.beta_programs = data.beta_programs;
            state.customer_country = data.customer_country;
            const clinicEntry = extractClinicEntry(data);
            state.rbac_roles = extractUserRoles(clinicEntry);
            state.is_admin = !!clinicEntry?.is_admin;
            state.permissions = buildPermissions(state.rbac_roles, state.rbac_role_catalog);
        },
        SET_RBAC_ROLE_CATALOG(state, catalog) {
            state.rbac_role_catalog = catalog
            state.rbacCatalogLoaded = true
            state.permissions = buildPermissions(state.rbac_roles, catalog)
        },
        SET_TOKEN(state) {
            state.jwtToken = sessionStorage.getItem("token")
        },
        SET_DATALOADING(state) {
            state.roleLoading = true;
            state.roleHasError = false;
        },
        SET_DATALOADED(state) {
            state.roleLoading = false;
            state.roleHasError = false;
        },
        SET_DATAERROR(state) {
            state.roleLoading = false;
            state.roleHasError = true;
        },

        SET_ATTRIBUTE(state, data) {
            Object.keys(data).forEach(key => { state[key] = data[key] })
        }
    },
    actions: {
        async getUserInfo({ state, commit, dispatch }) {
            commit('SET_DATALOADING')
            if (!state.jwtToken) {//get userinfo for the first time
                const userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
                if (userInfo?.customer_id) {
                    commit("SET_USERINFO", userInfo);
                    commit("SET_TOKEN");
                    commit('SET_DATALOADED')
                    // bootstrap.js already cached getUserInfoV2 into sessionStorage, so
                    // getDataFromApi is skipped here - the catalog still needs fetching.
                    await dispatch('getRbacRoles');
                    return;
                } else {// sessionStorage have no userinfo
                    await this.dispatch('myPractice/userInfo/getDataFromApi', null, { root: true });
                }
            } else {
                if (state.jwtToken != sessionStorage.getItem("token")) {//if user change account or clinic
                    commit('SET_DEFAULT')
                    const userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
                    //if sessionStorage has updated data
                    if (userInfo.clinic_id?.toString() != state.clinicId || userInfo.customer_id?.toString() != state.customer_id) {
                        commit("SET_USERINFO", userInfo);
                        commit("SET_TOKEN");
                        commit('SET_DATALOADED')
                        // SET_DEFAULT cleared the previous account's catalog - reload it
                        // for the new one.
                        await dispatch('getRbacRoles');
                    } else {
                        await this.dispatch('myPractice/userInfo/getDataFromApi', null, { root: true });
                    }

                } else {//nothing to do if jwttoken not change
                    commit('SET_DATALOADED')
                    await dispatch('getRbacRoles');
                }
            }
        },

        // Loads the role -> permission catalog. No-op for clinics that aren't on
        // the RBAC beta (their permissions come from legacyCanEdit instead) and
        // once it is in the store; retries if an earlier attempt failed.
        async getRbacRoles({ state, getters, commit }) {
            if (state.rbacCatalogLoaded || !getters.isRbacEnabled) return
            await fetchRoleCatalog(commit)
        },

        async getDataFromApi({ commit, dispatch }) {
            let response
            try {
                response = await getSettingDataV1("/utility/getUserInfoV2")
            } catch (error) {
                commit("SET_DATAERROR")
                console.log(error);
                return
            }

            commit("SET_USERINFO", response.data.result);
            commit("SET_TOKEN");
            commit('SET_DATALOADED');

            // Has to follow SET_USERINFO: whether the catalog is fetched at all
            // depends on the beta program it carries. Never rejects.
            await dispatch('getRbacRoles');

            // heap is injected by the host page and is absent in some environments,
            // so it must not gate the commits above: this used to sit inside a
            // .then() whose .catch() swallowed the TypeError into SET_DATAERROR.
            try {
                window.heap?.identify(response.data.result.customer_id + '_' + response.data.clinic_id);
                window.heap?.addUserProperties({ 'role': response.data.result.internal_role || [...response.data.result.user_roles] });
            } catch (error) {
                console.log(error);
            }
        },

        updateStoreData({ commit }, obj) {
            commit("SET_ATTRIBUTE", obj)

        }

    },

}


export default userInfo;